import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import {
  Button,
  Card,
  Input,
  LoadingSpinner,
  ProtectedRoute,
  PageHeader,
  StatusBadge,
  EmptyState
} from '../components/ui';
import { emailNotificationsAPI } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { usePlanFeature } from '../hooks/usePlanFeature';
import PlanFeatureUpgradePrompt from '../components/PlanFeatureUpgradePrompt';

type EmailLog = {
  id: string;
  type: string;
  periodStart: string;
  periodEnd: string;
  subject: string;
  status: 'sent' | 'failed' | 'skipped';
  errorMessage?: string | null;
  sentAt?: string | null;
  createdAt: string;
};

type ManualRecipient = { id?: string; email: string; name: string; isActive: boolean };

const DAYS = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' }
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const ROLE_OPTIONS = [
  { id: 'ADMIN', label: 'Administrador' },
  { id: 'MANAGER', label: 'Gerente' },
  { id: 'EMPLOYEE', label: 'Empleado' }
] as const;

function logStatusTone(status: string): 'success' | 'danger' | 'warning' | 'neutral' {
  switch (status) {
    case 'sent':
      return 'success';
    case 'failed':
      return 'danger';
    case 'skipped':
      return 'warning';
    default:
      return 'neutral';
  }
}

function logStatusLabel(status: string) {
  switch (status) {
    case 'sent':
      return 'Enviado';
    case 'failed':
      return 'Fallido';
    case 'skipped':
      return 'Omitido';
    default:
      return status;
  }
}

type EmailSettingsForm = {
  timezone: string;
  isActive: boolean;
  sendIfEmpty: boolean;
  weeklySummaryEnabled: boolean;
  weeklySummaryDay: number;
  weeklySummaryHour: number;
  pendingPaymentsEnabled: boolean;
  overdueInvoicesEnabled: boolean;
  dailyDigestEnabled: boolean;
  lowInventoryEnabled: boolean;
  newClientsSummaryEnabled: boolean;
  recipientRoles: string[];
  manualRecipients: ManualRecipient[];
  maxExtraEmailsPerUser: number;
};

function getEnabledEmailTypes(form: EmailSettingsForm): string[] {
  const types: string[] = [];
  if (form.weeklySummaryEnabled || form.lowInventoryEnabled || form.newClientsSummaryEnabled) {
    types.push('weekly_summary');
  }
  if (form.pendingPaymentsEnabled) types.push('pending_payments');
  if (form.overdueInvoicesEnabled) types.push('overdue_invoices');
  if (form.dailyDigestEnabled) types.push('daily_digest');
  return types;
}

function buildSettingsPayload(form: EmailSettingsForm) {
  return {
    ...form,
    manualRecipients: form.manualRecipients.filter((r) => r.email.trim())
  };
}

const ConfigurationEmailNotifications: React.FC = () => {
  const queryClient = useQueryClient();
  const { isAdmin } = usePermissions();
  const { enabled: hasEmailAutomation, isLoading: loadingPlanFeature } = usePlanFeature('emailAutomation');
  const [isDispatching, setIsDispatching] = useState(false);

  const [form, setForm] = useState({
    timezone: 'America/Caracas',
    isActive: true,
    sendIfEmpty: false,
    weeklySummaryEnabled: false,
    weeklySummaryDay: 1,
    weeklySummaryHour: 8,
    pendingPaymentsEnabled: false,
    overdueInvoicesEnabled: false,
    dailyDigestEnabled: false,
    lowInventoryEnabled: false,
    newClientsSummaryEnabled: false,
    recipientRoles: ['ADMIN', 'MANAGER'] as string[],
    manualRecipients: [] as ManualRecipient[],
    maxExtraEmailsPerUser: 3
  });

  const { data: settingsRes, isLoading: loadingSettings } = useQuery(
    'emailNotificationSettings',
    () => emailNotificationsAPI.getSettings(),
    {
      enabled: hasEmailAutomation,
      onSuccess: (res) => {
        const d = res.data?.data;
        if (!d) return;
        setForm({
          timezone: d.timezone || 'America/Caracas',
          isActive: d.isActive,
          sendIfEmpty: d.sendIfEmpty,
          weeklySummaryEnabled: d.weeklySummaryEnabled,
          weeklySummaryDay: d.weeklySummaryDay ?? 1,
          weeklySummaryHour: d.weeklySummaryHour ?? 8,
          pendingPaymentsEnabled: d.pendingPaymentsEnabled,
          overdueInvoicesEnabled: d.overdueInvoicesEnabled,
          dailyDigestEnabled: d.dailyDigestEnabled,
          lowInventoryEnabled: d.lowInventoryEnabled,
          newClientsSummaryEnabled: d.newClientsSummaryEnabled,
          recipientRoles: d.recipientRoles?.length ? d.recipientRoles : ['ADMIN', 'MANAGER'],
          manualRecipients: (d.manualRecipients || []).map((r: ManualRecipient) => ({
            email: r.email,
            name: r.name || '',
            isActive: r.isActive !== false
          })),
          maxExtraEmailsPerUser: d.maxExtraEmailsPerUser ?? 3
        });
      }
    }
  );

  const { data: logsRes, isLoading: loadingLogs } = useQuery(
    'emailNotificationLogs',
    () => emailNotificationsAPI.getLogs({ limit: 30 }),
    { enabled: hasEmailAutomation }
  );

  const logs: EmailLog[] = useMemo(() => logsRes?.data?.data || [], [logsRes]);

  const saveMutation = useMutation(
    (payload: Record<string, unknown>) => emailNotificationsAPI.updateSettings(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('emailNotificationSettings');
        toast.success('Configuración guardada');
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.message || err.message || 'Error al guardar');
      }
    }
  );

  const sendNowMutation = useMutation(
    (body: { types?: string[]; force?: boolean }) => emailNotificationsAPI.sendNow(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('emailNotificationLogs');
        toast.success('Envío solicitado');
      }
    }
  );

  const sendTestMutation = useMutation(
    (body: { types: string[] }) => emailNotificationsAPI.sendTest(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('emailNotificationLogs');
        toast.success('Correo de prueba enviado');
      }
    }
  );

  const retryMutation = useMutation((id: string) => emailNotificationsAPI.retryLog(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('emailNotificationLogs');
      toast.success('Reintento ejecutado');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || 'No se pudo reintentar');
    }
  });

  const handleSave = () => {
    saveMutation.mutate(buildSettingsPayload(form));
  };

  const persistSettings = async () => {
    await emailNotificationsAPI.updateSettings(buildSettingsPayload(form));
    await queryClient.invalidateQueries('emailNotificationSettings');
  };

  const handleSendTest = async () => {
    const types = getEnabledEmailTypes(form);
    if (types.length === 0) {
      toast.error('Active al menos un tipo de correo en la sección "Tipos de correo".');
      return;
    }
    setIsDispatching(true);
    try {
      await persistSettings();
      await sendTestMutation.mutateAsync({ types });
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Error en prueba');
    } finally {
      setIsDispatching(false);
    }
  };

  const handleSendNow = async () => {
    const types = getEnabledEmailTypes(form);
    if (types.length === 0) {
      toast.error('Active al menos un tipo de correo en la sección "Tipos de correo".');
      return;
    }
    setIsDispatching(true);
    try {
      await persistSettings();
      await sendNowMutation.mutateAsync({ types, force: true });
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Error en envío');
    } finally {
      setIsDispatching(false);
    }
  };

  const toggleRole = (role: string) => {
    setForm((prev) => {
      const has = prev.recipientRoles.includes(role);
      const next = has ? prev.recipientRoles.filter((r) => r !== role) : [...prev.recipientRoles, role];
      if (next.length === 0) return prev;
      return { ...prev, recipientRoles: next };
    });
  };

  const addManualRecipient = () => {
    setForm((prev) => ({
      ...prev,
      manualRecipients: [...prev.manualRecipients, { email: '', name: '', isActive: true }]
    }));
  };

  const updateManual = (index: number, patch: Partial<ManualRecipient>) => {
    setForm((prev) => {
      const copy = [...prev.manualRecipients];
      copy[index] = { ...copy[index], ...patch };
      return { ...prev, manualRecipients: copy };
    });
  };

  const removeManual = (index: number) => {
    setForm((prev) => ({
      ...prev,
      manualRecipients: prev.manualRecipients.filter((_, i) => i !== index)
    }));
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso denegado</h2>
          <p className="text-gray-600">Solo administradores pueden configurar notificaciones por correo.</p>
        </div>
      </div>
    );
  }

  if (!loadingPlanFeature && !hasEmailAutomation) {
    return (
      <ProtectedRoute permission="configuration.read">
        <div className="space-y-8 animate-fade-in-up max-w-5xl mx-auto">
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <Link
              to="/configuration"
              className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Configuración
            </Link>
          </div>
          <PlanFeatureUpgradePrompt
            feature="emailAutomation"
            backLink={{ to: '/configuration', label: 'Volver a configuración' }}
          />
        </div>
      </ProtectedRoute>
    );
  }

  if (loadingSettings && !settingsRes) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <ProtectedRoute permission="configuration.read">
      <div className="space-y-8 animate-fade-in-up max-w-5xl mx-auto">
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <Link
            to="/configuration"
            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Configuración
          </Link>
        </div>

        <PageHeader
          title="Notificaciones por correo"
          subtitle="Resúmenes semanales y recordatorios operativos. Los envíos se registran para auditoría."
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendTest}
                loading={isDispatching || sendTestMutation.isLoading}
                disabled={isDispatching}
              >
                <BeakerIcon className="h-4 w-4 mr-2" />
                Enviar prueba
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleSendNow}
                loading={isDispatching || sendNowMutation.isLoading}
                disabled={isDispatching}
              >
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                Enviar ahora
              </Button>
            </div>
          }
        />

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <EnvelopeIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-semibold text-slate-900">General</h2>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-primary-600"
              checked={form.isActive}
              onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
            />
            <span className="text-sm text-slate-700">Activar envíos programados (cron semanal)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer mt-4">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-primary-600"
              checked={form.sendIfEmpty}
              onChange={(e) => setForm((p) => ({ ...p, sendIfEmpty: e.target.checked }))}
            />
            <span className="text-sm text-slate-700">Enviar aunque no haya novedades</span>
          </label>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Programación semanal</h2>
          <p className="text-sm text-slate-500 mb-4">
            Día y hora en la zona horaria indicada. El sistema revisa cada 15 minutos.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Día</label>
              <select
                aria-label="Día de envío semanal"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.weeklySummaryDay}
                onChange={(e) =>
                  setForm((p) => ({ ...p, weeklySummaryDay: parseInt(e.target.value, 10) }))
                }
              >
                {DAYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Hora</label>
              <select
                aria-label="Hora de envío semanal"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.weeklySummaryHour}
                onChange={(e) =>
                  setForm((p) => ({ ...p, weeklySummaryHour: parseInt(e.target.value, 10) }))
                }
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Input
                label="Zona horaria (IANA)"
                value={form.timezone}
                onChange={(v) => setForm((p) => ({ ...p, timezone: v }))}
                placeholder="America/Caracas"
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Tipos de correo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
                checked={form.weeklySummaryEnabled}
                onChange={(e) => setForm((p) => ({ ...p, weeklySummaryEnabled: e.target.checked }))}
              />
              <span className="text-sm text-slate-700">Resumen semanal</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
                checked={form.pendingPaymentsEnabled}
                onChange={(e) => setForm((p) => ({ ...p, pendingPaymentsEnabled: e.target.checked }))}
              />
              <span className="text-sm text-slate-700">Pagos pendientes</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
                checked={form.overdueInvoicesEnabled}
                onChange={(e) => setForm((p) => ({ ...p, overdueInvoicesEnabled: e.target.checked }))}
              />
              <span className="text-sm text-slate-700">Facturas vencidas</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
                checked={form.newClientsSummaryEnabled}
                onChange={(e) => setForm((p) => ({ ...p, newClientsSummaryEnabled: e.target.checked }))}
              />
              <span className="text-sm text-slate-700">Incluir clientes nuevos en resumen</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
                checked={form.lowInventoryEnabled}
                onChange={(e) => setForm((p) => ({ ...p, lowInventoryEnabled: e.target.checked }))}
              />
              <span className="text-sm text-slate-700">Alerta inventario bajo (ataúdes)</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
                checked={form.dailyDigestEnabled}
                onChange={(e) => setForm((p) => ({ ...p, dailyDigestEnabled: e.target.checked }))}
              />
              <span className="text-sm text-slate-700">Digest diario (preparado, no activo)</span>
            </label>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Destinatarios</h2>
          <p className="text-sm text-slate-500 mb-3">
            Usuarios por rol (además de correos adicionales abajo). Cada usuario puede configurar sus
            propios correos y tipos en &quot;Mis preferencias&quot;.
          </p>
          <div className="mb-6 max-w-xs">
            <Input
              label="Máx. correos adicionales por usuario"
              value={String(form.maxExtraEmailsPerUser)}
              onChange={(v) => {
                const n = parseInt(v, 10);
                if (!Number.isNaN(n)) {
                  setForm((p) => ({ ...p, maxExtraEmailsPerUser: Math.min(20, Math.max(0, n)) }));
                }
              }}
              placeholder="3"
            />
            <p className="text-xs text-slate-500 mt-1">Entre 0 y 20 correos extra por usuario.</p>
          </div>
          <div className="flex flex-wrap gap-4 mb-6">
            {ROLE_OPTIONS.map((r) => (
              <label key={r.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-primary-600"
                  checked={form.recipientRoles.includes(r.id)}
                  onChange={() => toggleRole(r.id)}
                />
                <span className="text-sm text-slate-700">{r.label}</span>
              </label>
            ))}
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Correos adicionales</span>
              <Button type="button" variant="outline" size="sm" onClick={addManualRecipient}>
                Añadir
              </Button>
            </div>
            {form.manualRecipients.length === 0 ? (
              <p className="text-sm text-slate-500">No hay correos manuales adicionales.</p>
            ) : (
              form.manualRecipients.map((row, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                  <div className="md:col-span-5">
                    <Input
                      label="Email"
                      value={row.email}
                      onChange={(v) => updateManual(idx, { email: v })}
                      placeholder="correo@ejemplo.com"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <Input
                      label="Nombre (opcional)"
                      value={row.name}
                      onChange={(v) => updateManual(idx, { name: v })}
                    />
                  </div>
                  <div className="md:col-span-2 flex items-center gap-2 pb-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={row.isActive}
                        onChange={(e) => updateManual(idx, { isActive: e.target.checked })}
                      />
                      Activo
                    </label>
                  </div>
                  <div className="md:col-span-1">
                    <Button type="button" variant="outline" size="sm" onClick={() => removeManual(idx)}>
                      Quitar
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <div className="flex justify-end">
          <Button variant="danger" onClick={handleSave} loading={saveMutation.isLoading}>
            Guardar configuración
          </Button>
        </div>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Historial reciente</h2>
          {loadingLogs ? (
            <LoadingSpinner />
          ) : logs.length === 0 ? (
            <EmptyState
              title="Sin envíos registrados"
              description="Cuando se envíen correos automáticos o de prueba, aparecerán aquí."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="py-2 pr-4">Estado</th>
                    <th className="py-2 pr-4">Tipo</th>
                    <th className="py-2 pr-4">Asunto</th>
                    <th className="py-2 pr-4">Creado</th>
                    <th className="py-2 pr-4">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b border-slate-100">
                      <td className="py-2 pr-4">
                        <StatusBadge tone={logStatusTone(log.status)}>{logStatusLabel(log.status)}</StatusBadge>
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">{log.type}</td>
                      <td className="py-2 pr-4 max-w-xs truncate" title={log.subject}>
                        {log.subject}
                      </td>
                      <td className="py-2 pr-4 text-slate-600 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString('es-VE')}
                      </td>
                      <td className="py-2 pr-4">
                        {log.status === 'failed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => retryMutation.mutate(log.id)}
                            loading={retryMutation.isLoading}
                          >
                            Reintentar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </ProtectedRoute>
  );
};

export default ConfigurationEmailNotifications;
